import {
  ApolloClient,
  OperationVariables,
  WatchQueryFetchPolicy,
} from "@apollo/client";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { OperationDefinitionNode } from "graphql";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";

import { useAsync, useRefresh } from "@smart/itops-hooks-dom";

type FactoryOptions<D, I> = { map?: (d: D) => I };

type IncrementalFactoryOptions<D, I> = {
  map: (d: D) => I[];
  limit?: number;
};

type QueryOptions<D> = {
  client?: ApolloClient;
  reloadOnVisible?: boolean;
  onResult?: (data: D) => void;
  fetchPolicy?: WatchQueryFetchPolicy;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
  pollInterval?: number;
};

// This hook can only be used with paginated queries.
export const useIncrementalQueryFactory =
  <D, V extends OperationVariables, I>(
    queryDocument: TypedDocumentNode<D, V>,
    factoryOptions: IncrementalFactoryOptions<D, I>,
  ) =>
  (variables: V, options?: QueryOptions<D>) => {
    const limit = factoryOptions.limit || 100;
    const [result, setResult] = useState<I[]>([]);
    const tempResult = useRef<I[]>([]);
    /**
     * Use ref for loading state so the latest value can be accessed in the visibilitychange handler.
     * The queryLoading will trigger re-renders so the hook will return the latest value from the ref.
     */
    const loadingRef = useRef(true);
    const loadingTypeRef = useRef<"query" | "visibility" | "poll" | "refetch">(
      "query",
    );
    const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const [error, setError] = useState<Error | undefined>();
    const [key, refresh] = useRefresh();

    const [query, { loading: queryLoading, error: queryError }] = useLazyQuery(
      queryDocument,
      {
        client: options?.client,
        fetchPolicy: options?.fetchPolicy || "network-only",
        nextFetchPolicy:
          options?.nextFetchPolicy ||
          options?.fetchPolicy ||
          "cache-and-network",
      },
    );

    const fetchMore = useCallback(
      async (exclusiveStartKey: string | undefined) => {
        try {
          const { data: response } = await query({
            variables: { ...variables, limit, exclusiveStartKey },
          }).retain();
          if (response) {
            if (options?.onResult) {
              options.onResult(response);
            }
            const nextResult = factoryOptions.map(response);
            tempResult.current = [...tempResult.current, ...nextResult];
            if (loadingTypeRef.current === "query") {
              setResult(tempResult.current);
            }

            const queryName = (
              queryDocument.definitions[0] as OperationDefinitionNode
            ).name?.value as string;

            return {
              exclusiveStartKey: (
                response as { [key: string]: { exclusiveStartKey: string } }
              )[queryName].exclusiveStartKey,
            };
          }
        } catch (err) {
          setError(err as Error);
        }

        return { exclusiveStartKey: undefined };
      },
      [variables, limit, query],
    );

    useAsync(async () => {
      try {
        loadingRef.current = true;
        tempResult.current = [];
        setError(undefined);
        let currentOffset;
        let loadMore = true;

        while (loadMore) {
          const { exclusiveStartKey } = await fetchMore(currentOffset);
          currentOffset = exclusiveStartKey;
          loadMore = !!exclusiveStartKey;
        }
        setResult(tempResult.current);
      } catch (err) {
        setError(err as Error);
      } finally {
        loadingRef.current = false;
        if (options?.pollInterval) {
          clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            if (!loadingRef.current) {
              loadingTypeRef.current = "poll";
              refresh();
            }
          }, options.pollInterval);
        }
      }
    }, [key]);

    useEffect(() => {
      const handler = () => {
        if (!document.hidden) {
          if (!loadingRef.current) {
            loadingTypeRef.current = "visibility";
            refresh();
          }
        } else if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = undefined;
        }
      };

      if (options?.reloadOnVisible) {
        document.addEventListener("visibilitychange", handler);
      }

      return () => document.removeEventListener("visibilitychange", handler);
    }, [!!options?.reloadOnVisible]);

    const getLoading = () => {
      if (loadingTypeRef.current !== "query") {
        return false;
      }

      return loadingRef.current || queryLoading;
    };
    return {
      loading: getLoading(),
      refetching: loadingRef.current && loadingTypeRef.current !== "query",
      refetch: () => {
        if (!loadingRef.current) {
          loadingTypeRef.current = "refetch";
          refresh();
        }
      },
      error: error || queryError,
      result,
    };
  };

export const useQueryFactory =
  <D, V extends OperationVariables, I>(
    queryDocument: TypedDocumentNode<D, V>,
    factoryOptions?: FactoryOptions<D, I>,
  ) =>
  (variables: V, options?: QueryOptions<D>) => {
    const query = useQuery(queryDocument, {
      variables,
      client: options?.client,
      fetchPolicy: options?.fetchPolicy || "network-only",
      nextFetchPolicy:
        options?.nextFetchPolicy || options?.fetchPolicy || "cache-and-network",
      skip: options?.skip,
      pollInterval: options?.pollInterval,
    });

    useEffect(() => {
      if (query.data && options?.onResult) {
        options.onResult(query.data);
      }
    }, [query.data, options?.onResult]);

    useEffect(() => {
      const handler = () => {
        if (!document.hidden) {
          query.refetch().catch(console.error);
        }
      };

      if (options?.reloadOnVisible) {
        document.addEventListener("visibilitychange", handler);
      }

      return () => document.removeEventListener("visibilitychange", handler);
    }, [!!options?.reloadOnVisible]);

    const result = useMemo(
      () =>
        factoryOptions?.map && query.data
          ? factoryOptions.map(query.data)
          : undefined,
      [factoryOptions?.map, query.data],
    );

    return {
      ...query,
      result,
    };
  };
